import { useEffect, useMemo, useState } from "react";

import type { Market, PlaceOrderRequest, PlacePaperOrderRequest, Side, Token } from "../api/types";
import { useStatus } from "../hooks/useStatus";
import { partialNote } from "../lib/accounts";
import { decimalsForTick, format } from "../lib/money";
import {
  GTD_PRESETS,
  ORDER_TYPES,
  notional,
  snapPrice,
  stepPrice,
  validateDraft,
  type OrderDraft,
  type Problem,
  type ProblemField,
} from "../lib/order";
import { sharesForUsd } from "../lib/size";
import { best } from "../store/book";
import { useFormCohortPreview, useFormTarget } from "../store/useAccounts";
import { useBooks } from "../store/useBooks";
import { useOrders } from "../store/useOrders";
import { useSettings } from "../store/useSettings";
import { isEngaged } from "./KillSwitch";
import { Panel } from "./Panel";

/**
 * Форма ордера.
 *
 * Проверки повторяют серверные теми же формулировками (lib/order.ts) — но это
 * СОВЕТ: закон применяет трейдер, у которого есть стакан, сегодняшние
 * исполнения и приватный ключ. Поэтому кнопка отправки не блокируется при
 * взведённом стопе, а лишь предупреждает: оператор вправе отправить команду и
 * увидеть авторитетный отказ трейдера.
 */
interface Props {
  market: Market;
  tokens: Token[];
  /**
   * Выбранный исход. Поднят в MarketPage и общий с панелью быстрых действий:
   * два независимых «текущих токена» на одном экране — это способ продать то,
   * что собирался купить.
   */
  assetId: string;
  onAssetChange: (assetId: string) => void;
  /**
   * Уровень, выбранный кликом по лесенке. Несёт актив и номер клика: без актива
   * клик по чужой лесенке подставлял цену не того исхода, без номера — повторный
   * клик по той же цене не менял состояние и выглядел как сломанная лесенка.
   */
  picked?: { assetId: string; price: string; nonce: number } | null | undefined;
  /**
   * Пометка о подписке трейдера — из lib/tracking.ts, считает MarketPage.
   * Форма не решает её сама: для решения нужен момент чтения флага из
   * справочника, а он известен только владельцу запроса.
   */
  trackingNote?: string | undefined;
  /** Утилиты для карточки — форма живёт в сетке и должна тянуться до её низа. */
  className?: string;
}

export function OrderForm({
  market,
  tokens,
  assetId,
  onAssetChange,
  picked,
  trackingNote,
  className,
}: Props) {
  const [draft, setDraft] = useState<OrderDraft>({
    side: "BUY",
    price: "",
    size: "",
    orderType: "GTC",
    lifetimeSec: null,
  });
  /**
   * Сумма в долларах — то, что вводит оператор.
   *
   * draft.size при этом остаётся в SHARE: его проверяет validateDraft теми же
   * правилами, что и трейдер, и его же принимает биржа. Доллары не заменяют
   * размер, а ВЫЧИСЛЯЮТ его — по цене, которая тут же, в форме, и меняется.
   */
  const [usd, setUsd] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  // Свёрнутость живёт в НАСТРОЙКАХ, а не в локальном состоянии: это ответ на
  // вопрос «работаю ли я клавишами или мышью», и переспрашивать его при каждом
  // открытии вкладки незачем (см. lib/settings.ts, orderFormOpen).
  const open = useSettings((s) => s.settings.orderFormOpen);
  const setOpen = useSettings((s) => s.setOrderFormOpen);

  const status = useStatus();
  const place = useOrders((s) => s.place);
  const placePaper = useOrders((s) => s.placePaper);
  /**
   * Режим симуляции. Читается ЗДЕСЬ, а не приходит пропом: форма — один из двух
   * путей отправки, и оба обязаны видеть одну настройку. Проп означал бы, что
   * страница вправе развести их, а «быстрые кнопки пробуют, форма тратит» — это
   * ровно та ошибка, которой боятся.
   */
  const paperMode = useSettings((s) => s.settings.paperMode);
  // Цель — из того же места, что у быстрых кнопок: форма и кнопки обязаны
  // слать от имени одного и того же аккаунта.
  // Цель — СВОЯ у формы (селектор над ней), а не активная строка быстрых
  // действий: выбор у формы действует только на неё.
  const command = useFormTarget();
  const preview = useFormCohortPreview(usd.trim() || null);
  // Шаг берётся из СТАКАНА, а не из справочника: tick_size_change доходит до
  // стакана раньше, и по устаревшему шагу форма отвергала бы верную цену.
  const book = useBooks((s) => (assetId ? s.books[assetId] : undefined));
  const tickSize = book?.tickSize || market.tick_size;

  const constraints = useMemo(
    () => ({
      tickSize,
      minOrderSize: market.min_order_size,
      tradable: market.active && !market.closed,
    }),
    [tickSize, market.min_order_size, market.active, market.closed],
  );

  // Цена из лесенки подставляется как есть: она уже кратна шагу по построению,
  // прижимать её не нужно и незачем. Зависимость по nonce, а не по цене:
  // повторный клик по тому же уровню обязан сработать.
  //
  // Клик по уровню РАЗВОРАЧИВАЕТ свёрнутую форму. Иначе цена уезжала бы в
  // невидимое поле, а клик по лесенке — единственный способ поставить цену,
  // заведомо кратную шагу: молчаливый отклик выглядел бы как сломанный стакан.
  useEffect(() => {
    if (!picked) return;
    setDraft((d) => ({ ...d, price: picked.price }));
    setOpen(true);
  }, [picked?.nonce, picked?.price, setOpen]);

  /**
   * L1 текущей стороны: лучший bid для покупки, лучший ask для продажи.
   *
   * Тот же уровень, на который встаёт кнопка «Maker L1» соседней панели, и это
   * не совпадение: форма и панель — два способа отправить одну команду, и
   * умолчание у них обязано быть одно.
   */
  const l1 = useMemo(() => {
    if (!book) return null;
    const level = draft.side === "BUY" ? best(book.bids) : best(book.asks);
    return level === null ? null : format(level.p);
  }, [book, draft.side]);

  /**
   * Пустое поле цены заполняется текущим L1.
   *
   * Заполняется ТОЛЬКО пустое: набранную цену подменять нельзя, а «живая» цена,
   * ползающая в поле вслед за стаканом, увела бы ордер из-под пальца между
   * взглядом и нажатием. Поэтому же зависимость по `draft.price` — как только
   * поле перестало быть пустым, эффект молчит.
   */
  useEffect(() => {
    if (draft.price !== "" || l1 === null) return;
    setDraft((d) => (d.price === "" ? { ...d, price: l1 } : d));
  }, [draft.price, l1]);

  /**
   * Смена исхода или стороны ОЧИЩАЕТ цену, чтобы эффект выше подставил новый L1.
   *
   * Оставить прежнее число нельзя: цена одного исхода на другом означает
   * противоположную ставку (на бинарном рынке 0.52 по UP — это 0.48 по DOWN), а
   * цена покупки, поданная как цена продажи, встаёт по неверную сторону спреда.
   * Ровно тот же довод, по которому клик по лесенке несёт актив вместе с ценой.
   */
  useEffect(() => {
    setDraft((d) => (d.price === "" ? d : { ...d, price: "" }));
  }, [assetId, draft.side]);

  // Размер пересчитывается при КАЖДОМ изменении цены, а не только суммы:
  // оператор двигает цену стрелками и кликом по лесенке, и размер, посчитанный
  // по прошлой цене, потратил бы не ту сумму.
  useEffect(() => {
    const size = usd.trim() === "" ? "" : (sharesForUsd(usd, draft.price) ?? "");
    setDraft((d) => (d.size === size ? d : { ...d, size }));
  }, [usd, draft.price]);

  /**
   * В симуляции тип ордера ПРИНУДИТЕЛЬНО тейкерский.
   *
   * Мейкерскую заявку исполняет встречный поток, а не стакан, и симулировать её
   * проходом по книге нельзя (ADR-0031). Показать выбор, который ничего не
   * меняет, хуже, чем не показать его вовсе: оператор решил бы, что пробует
   * мейкерский вход, и сравнил бы с ним настоящую сделку.
   */
  const effective: OrderDraft = paperMode
    ? { ...draft, orderType: "FAK", lifetimeSec: null }
    : draft;

  const problems = useMemo(() => validateDraft(effective, constraints), [effective, constraints]);
  const total = notional(draft.price, draft.size);
  const engaged = isEngaged(status.data);
  const priceDigits = decimalsForTick(tickSize);

  function update(patch: Partial<OrderDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
    setSent(null);
    setError(null);
  }

  function step(dir: 1 | -1) {
    update({ price: stepPrice(draft.price || "0", tickSize, dir) });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    // Момент нажатия, монотонные часы. event.timeStamp ставится браузером в
    // момент ГЕНЕРАЦИИ события и потому захватывает занятость главного потока —
    // ту самую задержку, которую оператор чувствует, а замер от входа в
    // обработчик молча потерял бы.
    const clickedAtMono = e.timeStamp || performance.now();
    if (problems.length > 0 || sending || !assetId || !command.target) return;
    const target = command.target;

    setSending(true);
    setError(null);

    // Два пути расходятся ЗДЕСЬ и только здесь: всё, что выше, — одна и та же
    // цена, один и тот же размер и одни и те же проверки. Иначе симуляция
    // мерила бы не тот вход, который оператор делает в бою.
    const res = paperMode
      ? await placePaper(
          {
            condition_id: market.condition_id,
            asset_id: assetId,
            side: draft.side,
            price: draft.price,
            size: draft.size,
          } satisfies PlacePaperOrderRequest,
          target,
          { source: "form", clickedAtMono },
        )
      : await place(
          {
            condition_id: market.condition_id,
            asset_id: assetId,
            side: draft.side,
            price: draft.price,
            size: draft.size,
            order_type: draft.orderType,
            ...(draft.orderType === "GTD" && draft.lifetimeSec !== null
              ? { expires_at: new Date(Date.now() + draft.lifetimeSec * 1000).toISOString() }
              : {}),
          } satisfies PlaceOrderRequest,
          target,
          { source: "form", clickedAtMono },
        );
    setSending(false);
    if (res.ok) {
      setSent(
        (paperMode
          ? "проба отправлена — налив считается по стакану, деньги не тратятся"
          : "команда отправлена — ждём подтверждения трейдера") + partialNote(res.partial),
      );
      // Размер не сбрасываем: оператор обычно ставит серию ордеров одного
      // размера, и обнулённое поле заставляло бы набирать его каждый раз.
      setDraft((d) => ({ ...d, price: "" }));
    } else {
      setError(res.error);
    }
  }

  return (
    <Panel
      title={paperMode ? "Новый ордер · проба" : "Новый ордер"}
      subtitle={trackingNote}
      className={className}
      // Плотная карточка: форма стоит в ЛИПКОЙ полосе, её отступы оплачены всё
      // время работы с рынком, а не однажды при прокрутке.
      dense
      collapsed={!open}
      /*
        Сворачивание — для оператора, который работает КЛАВИШАМИ: быстрый путь
        закрывает почти все сделки, и держать рядом с ним развёрнутую форму
        незачем. Умолчание всё же «развёрнуто»: это единственный способ задать
        свою цену, GTD, FOK и FAK, и прятать его у того, кто ещё не выбирал,
        нельзя.

        Высоту полосы это НЕ уменьшает — её задаёт левая колонка, которая выше.
        Выигрыш тут другой: экран перестаёт показывать форму тому, кто ей не
        пользуется, а точный путь остаётся в одном нажатии.
      */
      actions={
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
          title={
            open
              ? "свернуть форму: быстрые действия и клавиши работают без неё"
              : "развернуть форму — своя цена, GTD, FOK и FAK"
          }
        >
          {open ? "▾" : "▸"}
        </button>
      }
    >
      <form className="flex flex-col gap-2" onSubmit={(e) => void submit(e)}>
        <div className="flex gap-1">
          {(["BUY", "SELL"] as Side[]).map((side) => (
            <button
              key={side}
              type="button"
              onClick={() => update({ side })}
              className="flex-1 rounded border px-3 py-1.5 text-sm font-medium"
              style={{
                borderColor:
                  draft.side === side
                    ? side === "BUY"
                      ? "var(--color-buy)"
                      : "var(--color-sell)"
                    : "var(--color-border)",
                color:
                  draft.side === side
                    ? side === "BUY"
                      ? "var(--color-buy)"
                      : "var(--color-sell)"
                    : "var(--color-muted)",
              }}
            >
              {side === "BUY" ? "Купить" : "Продать"}
            </button>
          ))}
        </div>

        {tokens.length > 1 && (
          /* Кнопки, а не выпадающий список: выбор из двух значений стоил двух
             кликов и открытого меню поверх формы. Цвет тот же, что у исхода на
             панели быстрых действий (UP зелёный, DOWN красный) — одно понятие
             обязано выглядеть одинаково на соседних блоках. Мельче, чем
             купить/продать: сторона решает больше. */
          <div className="flex gap-1">
            {tokens.map((t, i) => {
              const chosen = t.asset_id === assetId;
              const tone = i === 0 ? "var(--color-buy)" : "var(--color-sell)";
              return (
                <button
                  key={t.asset_id}
                  type="button"
                  onClick={() => onAssetChange(t.asset_id)}
                  className="flex-1 rounded border px-2 py-1 text-xs font-medium"
                  style={{
                    borderColor: chosen ? tone : "var(--color-border)",
                    color: chosen ? tone : "var(--color-muted)",
                  }}
                >
                  {(t.outcome || `исход ${t.outcome_index}`).toUpperCase()}
                </button>
              );
            })}
          </div>
        )}

        {/* Шаг и число знаков — СПРАВА от кнопок, а не в подписи: в колонку
            метки они не влезают, а убрать их нельзя — ими объясняется отказ
            проверки «цена не кратна шагу». */}
        <Field label="Цена" problem={problemOf(problems, "price")}>
          <div className="flex items-center gap-1">
            <input
              value={draft.price}
              onChange={(e) => update({ price: e.target.value })}
              onBlur={() => draft.price && update({ price: snapPrice(draft.price, tickSize) })}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  step(1);
                } else if (e.key === "ArrowDown") {
                  e.preventDefault();
                  step(-1);
                }
              }}
              inputMode="decimal"
              placeholder="0.52"
              className={inputClass}
            />
            <StepButton label="−" onClick={() => step(-1)} />
            <StepButton label="+" onClick={() => step(1)} />
            <span
              className="num shrink-0 whitespace-nowrap"
              title={`цена обязана быть кратна шагу ${tickSize}; знаков после точки — ${priceDigits}`}
            >
              шаг {tickSize}
            </span>
          </div>
        </Field>

        <Field
          label="Сумма, $"
          problem={
            problemOf(problems, "size") ??
            // Своя строка у этого случая была: сумма набрана, а размер не
            // посчитался. Это отказ, а не подсказка, и место ему в слоте
            // замечаний поля, а не отдельным рядом формы.
            (usd.trim() !== "" && draft.size === ""
              ? "по этой цене суммы не хватает даже на 0.01 share"
              : undefined)
          }
        >
          <input
            value={usd}
            onChange={(e) => {
              setUsd(e.target.value);
              setSent(null);
              setError(null);
            }}
            inputMode="decimal"
            placeholder="10"
            className={inputClass}
          />
        </Field>

        <Field
          label="Тип"
          problem={
            paperMode
              ? "проба бывает только тейкерской: мейкерскую заявку исполняет встречный поток, а не стакан"
              : problemOf(problems, "lifetime")
          }
        >
          <div className="flex gap-1">
            {ORDER_TYPES.map((info) => (
              <button
                key={info.type}
                type="button"
                disabled={paperMode}
                onClick={() =>
                  update({
                    orderType: info.type,
                    lifetimeSec:
                      info.type === "GTD" ? (draft.lifetimeSec ?? GTD_PRESETS[0]!.sec) : null,
                  })
                }
                className="flex-1 rounded border px-2 py-1 text-xs disabled:opacity-40"
                style={{
                  borderColor:
                    effective.orderType === info.type ? "var(--color-accent)" : "var(--color-border)",
                  color: effective.orderType === info.type ? "inherit" : "var(--color-muted)",
                }}
                // Подсказка целиком, включая разбор: постоянная строка под
                // кнопками отвечала на «что я выбрал», но занимала ряд у формы,
                // которая стоит в липкой полосе. Наведение стоит дешевле ряда, а
                // читают это описание один раз — когда разбираются, а не в
                // каждой сделке.
                title={`${info.type} — ${info.role}: ${info.summary}\n\n${info.detail}`}
              >
                {info.type}
              </button>
            ))}
          </div>
        </Field>

        {effective.orderType === "GTD" && (
          <div className="flex items-center gap-1">
            {GTD_PRESETS.map((p) => (
              <button
                key={p.sec}
                type="button"
                onClick={() => update({ lifetimeSec: p.sec })}
                className="rounded border px-2 py-1 text-xs"
                style={{
                  borderColor:
                    draft.lifetimeSec === p.sec ? "var(--color-accent)" : "var(--color-border)",
                  color: draft.lifetimeSec === p.sec ? "inherit" : "var(--color-muted)",
                }}
              >
                {p.label}
              </button>
            ))}
            {/* Биржа гасит GTD на минуту раньше заявленного; здесь честно
                показано, сколько ордер проживёт на самом деле. */}
            <span className="ml-1 text-xs text-[var(--color-muted)]">
              биржа гасит на минуту раньше
            </span>
          </div>
        )}

        {problemOf(problems, "market") && (
          <p className="text-xs text-[var(--color-sell)]">{problemOf(problems, "market")}</p>
        )}

        {/* Размер и потраченное — НА КНОПКЕ, а не строкой над ней.
            На бирже окажется именно этот размер, он же попадёт в список ордеров,
            и узнать о нём после отправки — значит узнать поздно. Прежде у него
            была своя строка под полем суммы; она стоила ряда в липкой полосе, а
            здесь число стоит ровно там, куда смотрят перед нажатием.

            Потраченное отличается от названного на округление размера вниз: $10
            по 0.52 — это 19.23 share и 9.99 USD. */}
        <button
          type="submit"
          disabled={problems.length > 0 || sending || !assetId || !command.target}
          className="flex items-baseline justify-center gap-2 rounded px-3 py-1.5 text-sm font-medium disabled:opacity-40"
          /* В симуляции кнопка ЯНТАРНАЯ и подписана иначе. Это последнее место,
             где оператор смотрит перед нажатием, и цвет здесь стоит дороже, чем
             в любом баннере. */
          style={{
            backgroundColor: paperMode
              ? "var(--color-warn, #fbbf24)"
              : draft.side === "BUY"
                ? "var(--color-buy)"
                : "var(--color-sell)",
            color: "black",
          }}
        >
          {sending
            ? "отправляется…"
            : `${draft.side === "BUY" ? "Купить" : "Продать"}${paperMode ? " (проба)" : ""}`}
          {!sending && draft.size !== "" && (
            <span className="num text-xs opacity-75">
              {draft.size} share{total === null ? "" : ` · $${total}`}
            </span>
          )}
        </button>

        {/* Кнопка НЕ блокируется стопом: закон у трейдера, и его отказ оператор
            обязан увидеть — иначе интерфейс молча решает за него. */}
        {engaged && !paperMode && (
          <p className="text-xs text-amber-300">
            аварийный стоп включён — трейдер отклонит ордер
          </p>
        )}
        {/* Пробе стоп НЕ мешает, и молчать об этом нельзя: смысл симуляции —
            «что было бы, если бы мы торговали», и она обязана считаться именно
            тогда, когда торговля остановлена. */}
        {engaged && paperMode && (
          <p className="text-xs text-[var(--color-muted)]">
            аварийный стоп включён — пробе он не мешает, отметка уйдёт в журнал
          </p>
        )}
        {/* Без цели кнопка недоступна, и причина обязана быть видна: молча
            серая кнопка выглядела бы поломкой. */}
        {command.problem && <p className="text-xs text-amber-300">{command.problem}</p>}
        {/* Когортой каждый аккаунт ставит полный размер: сумма на кнопке —
            не весь риск, и итог обязан быть виден до нажатия. */}
        {preview && <p className="text-xs text-amber-300">когортой: {preview.text}</p>}
        {error && <p className="text-xs text-[var(--color-sell)]">{error}</p>}
        {sent && <p className="text-xs text-[var(--color-muted)]">{sent}</p>}
      </form>
    </Panel>
  );
}

const inputClass =
  "min-w-0 flex-1 rounded border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 " +
  "text-sm outline-none placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)]";

function StepButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-8 rounded border border-[var(--color-border)] text-sm text-[var(--color-muted)] hover:text-white"
    >
      {label}
    </button>
  );
}

/**
 * Поле формы: подпись СЛЕВА от контрола, а не над ним.
 *
 * Стопкой это стоило по ряду на поле — шестьдесят пикселей в липкой полосе,
 * то есть шестьдесят пикселей, отнятых у ленты и накопления навсегда. Подпись
 * читают один раз, когда заполняют, и слева она читается так же.
 *
 * Замечание при этом остаётся ПОД контролом и во всю ширину: оно длиннее
 * подписи, а в колонку метки не влезло бы и рвалось бы посреди слова.
 */
function Field({
  label,
  problem,
  children,
}: {
  label: string;
  problem: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <label className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-x-2 gap-y-1 text-xs text-[var(--color-muted)]">
      <span>{label}</span>
      {children}
      {problem && (
        <span className="col-start-2 text-[var(--color-sell)]">{problem}</span>
      )}
    </label>
  );
}

/**
 * Текст проблемы для показа.
 *
 * Незаполненные поля молчат: их проблема блокирует кнопку, но красная строка на
 * форме, к которой ещё не притронулись, — это шум, который приучает не читать
 * ошибки вообще.
 */
function problemOf(problems: Problem[], field: ProblemField): string | undefined {
  const found = problems.find((p) => p.field === field);
  return found && !found.blank ? found.message : undefined;
}

